/**
 * Rendered box and baseline for text layers, taken by mounting the doc off-screen. Both beat what
 * the model carries: a hugged axis parses as 0, and the analytic baseline in `text-layer-data.ts`
 * only holds for simple layers. See this directory's AGENTS.md.
 */

import {
  type Doc,
  type Layer,
  textHugsHeight,
  textHugsWidth,
} from "@hightouch/lib/ad-studio/html-editor/model";
import { serializeDoc } from "@hightouch/lib/ad-studio/html-editor/serialize";

import {
  AD_CSS_ISOLATION_CLASS,
  AD_CSS_ISOLATION_CSS,
} from "src/pages/ad-studio/components/ad-css-isolation";

export type TextMeasurement = {
  width: number;
  height: number;
  /** First baseline, down from the layer's top edge. `null` when the layer has no text to sit on. */
  baseline: number | null;
};

const HOST_STYLE = [
  "position:fixed",
  "left:0",
  "top:0",
  "overflow:hidden",
  "visibility:hidden",
  "pointer-events:none",
  "z-index:-1",
].join(";");

/** Zero-size and baseline-aligned, so its own top edge sits exactly on the line's baseline. */
const PROBE_STYLE =
  "display:inline-block;width:0;height:0;vertical-align:baseline";

/**
 * Needs the ad's faces already in `document.fonts` — a fallback face measures a different width.
 * Best-effort: an unmeasurable layer is absent from the map.
 */
export function measureTextLayers(doc: Doc): Map<string, TextMeasurement> {
  const measured = new Map<string, TextMeasurement>();
  if (typeof document === "undefined") return measured;
  const ids = textLayerIds(doc.layers);
  if (ids.length === 0) return measured;

  const host = document.createElement("div");
  host.className = AD_CSS_ISOLATION_CLASS;
  host.setAttribute(
    "style",
    `${HOST_STYLE};width:${doc.width}px;height:${doc.height}px`,
  );
  // Sibling of the markup, not a descendant: the reset's `all: revert` would wipe it.
  const style = document.createElement("style");
  style.textContent = AD_CSS_ISOLATION_CSS;
  try {
    document.head.append(style);
    // The ids are how each measurement finds its layer.
    host.innerHTML = serializeDoc(doc, { layerIds: true });
    stripImageSources(host);
    document.body.append(host);
    for (const id of ids) {
      const el = host.querySelector(`[data-ht-id="${CSS.escape(id)}"]`);
      // The layout box, not `getBoundingClientRect`'s axis-aligned box for a rotated layer.
      if (!(el instanceof HTMLElement)) continue;
      if (el.offsetWidth <= 0 && el.offsetHeight <= 0) continue;
      measured.set(id, {
        width: el.offsetWidth,
        height: el.offsetHeight,
        baseline: measureFirstBaseline(el),
      });
    }
  } finally {
    host.remove();
    style.remove();
  }
  return measured;
}

/**
 * The probe wraps the first text node so it shares that text's inline context. `null` if inserting
 * it disturbed the layout. Layout offsets, not `getBoundingClientRect`: they are transform-free,
 * the same layer-local space `text-layer-data.ts` maps through `Trnf`. See AGENTS.md.
 */
function measureFirstBaseline(el: HTMLElement): number | null {
  const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
  let textNode = walker.nextNode();
  while (textNode && !textNode.textContent?.trim())
    textNode = walker.nextNode();
  if (!textNode?.parentNode) return null;

  const heightBefore = el.offsetHeight;
  const wrapper = document.createElement("span");
  textNode.parentNode.insertBefore(wrapper, textNode);
  wrapper.append(textNode);
  const probe = document.createElement("span");
  probe.setAttribute("style", PROBE_STYLE);
  wrapper.insertBefore(probe, wrapper.firstChild);
  try {
    if (el.offsetHeight !== heightBefore) return null;
    const baseline = layoutTopOf(probe) - layoutTopOf(el);
    return Number.isFinite(baseline) ? baseline : null;
  } finally {
    probe.remove();
    wrapper.replaceWith(textNode);
  }
}

/**
 * A node's top border edge in layout space, walking the `offsetParent` chain. Each parent's
 * `clientTop` converts its padding edge — what `offsetTop` measures from — back to its border edge,
 * so sums stay comparable between two nodes on the same chain.
 */
function layoutTopOf(node: HTMLElement): number {
  let top = 0;
  let current: Element | null = node;
  while (current instanceof HTMLElement) {
    top += current.offsetTop;
    const parent = current.offsetParent;
    if (parent instanceof HTMLElement) top += parent.clientTop;
    current = parent;
  }
  return top;
}

/** The measured boxes written back onto the hugged axes, other axes untouched. */
export function withMeasuredTextBoxes(
  doc: Doc,
  measured: Map<string, TextMeasurement>,
): Doc {
  if (measured.size === 0) return doc;
  return {
    ...doc,
    layers: doc.layers.map((l) => withMeasuredLayer(l, measured)),
  };
}

function withMeasuredLayer(
  layer: Layer,
  measured: Map<string, TextMeasurement>,
): Layer {
  if (layer.type === "group") {
    return {
      ...layer,
      children: layer.children.map((child) =>
        withMeasuredLayer(child, measured),
      ),
    };
  }
  const box = measured.get(layer.id);
  if (!box) return layer;
  return {
    ...layer,
    ...(textHugsWidth(layer) ? { width: box.width } : {}),
    ...(textHugsHeight(layer) ? { height: box.height } : {}),
  };
}

/** Every text layer: a hugged one needs its box, and all of them need a baseline. */
function textLayerIds(layers: Layer[]): string[] {
  const ids: string[] = [];
  for (const layer of layers) {
    if (layer.type === "group") {
      ids.push(...textLayerIds(layer.children));
      continue;
    }
    if (layer.type === "text" && layer.id) ids.push(layer.id);
  }
  return ids;
}

/** Nothing is painted and `ad:` refs need the inlining pass; the CSS box is unchanged either way. */
function stripImageSources(host: HTMLElement): void {
  for (const img of host.querySelectorAll("img")) {
    img.removeAttribute("src");
    img.removeAttribute("srcset");
  }
}
